import React, { useState, useEffect } from 'react';
import api from '../../http';

const StationEntryForm = ({ eventId, onStationAdded }) => {
  const [mode, setMode] = useState('template'); // 'template' or 'custom'
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Data Sources
  const [templates, setTemplates] = useState([]);
  const [skills, setSkills] = useState([]);

  // Template Form State
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Custom Form State
  const [customStation, setCustomStation] = useState({
    name: '',
    station_number: '', // Default auto assigned
    selectedSkills: [], // [{ id, name, mode }]
    saveAsTemplate: false
  });
  const [currentSkillId, setCurrentSkillId] = useState('');

  useEffect(() => {
    fetchResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchResources = async () => {
    try {
      const [tplRes, skillRes, stationsRes] = await Promise.all([
        api.get('/api/admin/station-templates'),
        api.get('/api/admin/skills'),
        api.get(`/api/test-events/${eventId}/stations`) // To guess next number
      ]);
      setTemplates(tplRes.data);
      setSkills(skillRes.data);

      // Auto-set next station number
      const nextNum = (stationsRes.data.length || 0) + 1;
      setCustomStation((prev) => ({ ...prev, station_number: nextNum }));
    } catch (err) {
      console.error(err);
      setError('Failed to load templates or skills.');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSubmit = async () => {
    if (!selectedTemplateId) return;
    setSubmitting(true);
    setError(null);

    try {
      // We need to know the station number.
      // We can fetch active stations again to be safe, or just send what we have?
      // "Add Station" usually appends.
      // But let's verify what the backend expects.
      // The backend `addStationToEvent` expects `station_number`.
      // Let's get the latest count first to be safe, or just ask user?
      // Existing Admin.js passed `stations.length + 1`.
      // I'll fetch current count quickly to ensure accuracy.
      const stnRes = await api.get(`/api/test-events/${eventId}/stations`);
      const nextNumber = stnRes.data.length + 1;

      await api.post(`/api/test-events/${eventId}/stations`, {
        station_template_id: selectedTemplateId,
        station_number: nextNumber
        // Backend handles filling defaults from template
      });

      if (onStationAdded) onStationAdded();
      setSelectedTemplateId('');
      // Update guessed number for custom form too
      setCustomStation((prev) => ({ ...prev, station_number: nextNumber + 1 }));
      alert('Station added successfully!');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add station.');
    } finally {
      setSubmitting(false);
    }
  };

  const addSkillToCustom = () => {
    if (!currentSkillId) return;
    const skill = skills.find((s) => s.id === parseInt(currentSkillId));
    if (!skill) return;
    if (customStation.selectedSkills.find((s) => s.id === skill.id)) return;

    setCustomStation((prev) => ({
      ...prev,
      selectedSkills: [
        ...prev.selectedSkills,
        { id: skill.id, name: skill.name, mode: 'individual' }
      ]
    }));
    setCurrentSkillId('');
  };

  const removeSkillFromCustom = (id) => {
    setCustomStation((prev) => ({
      ...prev,
      selectedSkills: prev.selectedSkills.filter((s) => s.id !== id)
    }));
  };


  const handleCustomSubmit = async () => {
    if (!customStation.name) {
      setError('Station Name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      // Prepare payload
      // Backend `addStationToEvent` supports `skill_ids` array, but seemingly only IDs.
      // It uses:
      // if (Array.isArray(skill_ids) && skill_ids.length > 0) ...
      // And inserts with `testing_mode: 'individual'` hardcoded (line 104 in controller fallback, wait...)
      // Let's re-read controller line 493:
      // const stationSkills = skill_ids.map((skillId, index) => ({ ..., skill_id: skillId, ... }));
      // It DOES NOT accept mode in `skill_ids` array if it's just an array of IDs.
      // However, `createWizard` handled complex objects. `addStationToEvent` seems simpler.
      // Wait, looking at controller code:
      // It doesn't seem to support custom skill modes in `addStationToEvent` via `skill_ids`.
      // But wait! `createWizard` logic was complex.
      // `addStationToEvent` (line 423) reads `skill_ids`.
      // Line 493: iterates `skill_ids`.
      // It inserts into `test_station_skills`.
      // Does it set `testing_mode`?
      // The `insert` call on 500 implies it uses default schema values?
      // Schema probably defaults `testing_mode` to 'individual'.

      // CONSTRAINT: The current `addStationToEvent` endpoint MIGHT NOT support `testing_mode` per skill for custom stations yet.
      // It only supports a list of IDs.
      // If I want to support modes, I might need to:
      // 1. Update backend (preferred if I could, but I'm frontend focused right now?)
      // 2. Or call `updateEventStation` or calls to `test_station_skills` after creation?
      // Let's check if there is a separate endpoint for station skills.
      // There isn't one listed in `test-events.js` explicitly for adding skills to a station,
      // BUT `updateEventStation` is for station properties.

      // Workaround: We will send `skill_ids` for now.
      // If the user wants specific modes, we might need a better backend endpoint.
      // BUT, `createWizard` did it manually.

      // Let's stick to sending `skill_ids`.
      const payload = {
        name: customStation.name,
        station_number: customStation.station_number, // We should trust user or fetch
        skill_ids: customStation.selectedSkills.map((s) => s.id),
        // We transmit common modes if possible? No.
        testing_mode: 'individual', // Default for station
        max_candidates_at_station: 1,
        time_between_candidates_seconds: 0
      };

      await api.post(`/api/test-events/${eventId}/stations`, payload);

      if (onStationAdded) onStationAdded();

      // Reset custom form partials
      setCustomStation((prev) => ({
        name: '',
        station_number: parseInt(prev.station_number) + 1,
        selectedSkills: [],
        saveAsTemplate: false
      }));
      alert('Custom station added!');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to add custom station.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '1rem'
      }}
    >
      <h3
        style={{
          marginTop: 0,
          marginBottom: '1rem',
          borderBottom: '1px solid #eee',
          paddingBottom: '0.5rem'
        }}
      >
        Add Station
      </h3>

      {error && (
        <div className="alert danger" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={`btn btn-sm ${mode === 'template' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setMode('template')}
        >
          From Template
        </button>
        <button
          className={`btn btn-sm ${mode === 'custom' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setMode('custom')}
        >
          Custom
        </button>
      </div>

      {mode === 'template' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Select Template
            </label>
            <select
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #cbd5e0',
                borderRadius: '4px'
              }}
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <option value="">-- Choose a Station Template --</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={!selectedTemplateId || submitting}
            onClick={handleTemplateSubmit}
          >
            {submitting ? 'Adding...' : 'Add Station'}
          </button>
        </div>
      )}

      {mode === 'custom' && (
        <div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '0.2rem' }}>
              Station Number
            </label>
            <input
              type="number"
              className="input"
              style={{ width: '100%' }}
              value={customStation.station_number}
              onChange={(e) =>
                setCustomStation({ ...customStation, station_number: e.target.value })
              }
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '0.2rem' }}>
              Station Name*
            </label>
            <input
              className="input"
              style={{ width: '100%' }}
              value={customStation.name}
              onChange={(e) => setCustomStation({ ...customStation, name: e.target.value })}
              placeholder="e.g. Rapid Intervention"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.9rem', marginBottom: '0.2rem' }}>
              Skills
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <select
                style={{ flex: 1, padding: '0.3rem' }}
                value={currentSkillId}
                onChange={(e) => setCurrentSkillId(e.target.value)}
              >
                <option value="">Select Skill...</option>
                {skills.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <button className="btn btn-sm btn-secondary" onClick={addSkillToCustom}>
                Add
              </button>
            </div>

            <div
              style={{
                background: '#f8f9fa',
                padding: '0.5rem',
                borderRadius: '4px',
                minHeight: '50px'
              }}
            >
              {customStation.selectedSkills.length === 0 && (
                <span style={{ color: '#999', fontSize: '0.85rem' }}>No skills selected.</span>
              )}
              {customStation.selectedSkills.map((s) => (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '0.9rem',
                    marginBottom: '0.25rem'
                  }}
                >
                  <span>{s.name}</span>
                  <button
                    onClick={() => removeSkillFromCustom(s.id)}
                    style={{
                      color: '#e53e3e',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={!customStation.name || submitting}
            onClick={handleCustomSubmit}
          >
            {submitting ? 'Creating...' : 'Create Station'}
          </button>
        </div>
      )}
    </div>
  );
};

export default StationEntryForm;
